import {
  AlertDialog,
  AlertDialogBody,
  AlertDialogContent,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogOverlay,
  Box,
  Button,
  Flex,
  FormControl,
  FormLabel,
  Spinner,
  Switch,
  Text,
  useDisclosure,
} from "@chakra-ui/react";
import { useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useConfirmSourcingEventClassificationMutation } from "../../../../../autogen/bff-api";
import { coordinatorRegistry } from "../../../../../common/editing/save-coordinator";
import { useApiError } from "../../../../../common/errors/useApiError";

interface Props {
  eventId: string;
  isAnnounced: boolean;
  hasInvitedSuppliers: boolean;
}

export const AnnounceOnDoffinSwitch = ({ eventId, isAnnounced, hasInvitedSuppliers }: Props) => {
  const { t } = useTranslation();
  const [confirmClassification, { isLoading }] = useConfirmSourcingEventClassificationMutation();
  const { trigger } = useApiError();
  const { isOpen, onOpen, onClose } = useDisclosure();
  const cancelRef = useRef<HTMLButtonElement>(null);
  const [pending, setPending] = useState<boolean | null>(null);
  const switchId = `announce-on-doffin-${eventId}`;

  const apply = async (announce: boolean) => {
    try {
      await coordinatorRegistry.flush(eventId);
      const changed = await confirmClassification({
        eventId,
        confirmClassificationRequest: { announcePartIOnDoffin: announce },
      }).unwrap();
      coordinatorRegistry.get(eventId)?.setServerState(changed);
      onClose();
    } catch (e) {
      trigger(e);
    }
  };

  const choose = (announce: boolean) => {
    const discardsSomething = announce ? hasInvitedSuppliers : true;
    if (!discardsSomething) {
      void apply(announce);
      return;
    }
    setPending(announce);
    onOpen();
  };

  return (
    <Box borderWidth="1px" borderColor="smBorder" borderRadius="lg" bg="white" p="5" mt="4">
      <FormControl display="flex" alignItems="flex-start" justifyContent="space-between" gap="6">
        <Box>
          <FormLabel htmlFor={switchId} mb="1" cursor="pointer">
            <Flex align="center" gap="2">
              <Text fontSize="sm" fontWeight="semibold">
                {t("Announce on Doffin")}
              </Text>
              {isLoading && <Spinner size="xs" />}
            </Flex>
          </FormLabel>
          <Text fontSize="sm" color="smMuted">
            {isAnnounced
              ? t("The procurement is announced on Doffin as a Del I procurement, open to every supplier. Del I has no standstill period, no ESPD and no Del II procedures.")
              : t("Del I has no duty to announce. Turn this on to announce the procurement voluntarily on Doffin, so every supplier can find it.")}
          </Text>
        </Box>
        <Switch
          id={switchId}
          isChecked={isAnnounced}
          isDisabled={isLoading}
          onChange={(e) => choose(e.target.checked)}
          colorScheme="teal"
          mt="1"
        />
      </FormControl>

      <AlertDialog isOpen={isOpen} leastDestructiveRef={cancelRef} onClose={onClose}>
        <AlertDialogOverlay>
          <AlertDialogContent>
            <AlertDialogHeader fontSize="lg" fontWeight="bold">
              {pending ? t("Announce on Doffin?") : t("Stop announcing on Doffin?")}
            </AlertDialogHeader>
            <AlertDialogBody>
              <Text fontSize="sm">
                {pending
                  ? t("The suppliers you have invited are discarded. The title, value, contract type, requirements and documents are kept.")
                  : t("The applicable part, CPV codes, the procedure and the notice data are discarded. The governing regulation, title, value, contract type, requirements and documents are kept.")}
              </Text>
              <Text fontSize="sm" mt="2">
                {t("This cannot be undone.")}
              </Text>
            </AlertDialogBody>
            <AlertDialogFooter>
              <Button ref={cancelRef} onClick={onClose} variant="ghost">
                {t("Cancel")}
              </Button>
              <Button
                colorScheme="red"
                ml="3"
                isLoading={isLoading}
                onClick={() => pending != null && void apply(pending)}
              >
                {pending ? t("Announce on Doffin") : t("Stop announcing")}
              </Button>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialogOverlay>
      </AlertDialog>
    </Box>
  );
};
